import React, { useEffect, useMemo, useState } from "react";

const getUserData = (selectedUser) => ({
  id: selectedUser?.user?._id || selectedUser?._id,
  name:
    `${selectedUser?.user?.firstname || selectedUser?.firstname || ""} ${
      selectedUser?.user?.lastname || selectedUser?.lastname || ""
    }`.trim() || "Selected user",
  email: selectedUser?.user?.username || selectedUser?.username || "",
  role: selectedUser?.user?.role || selectedUser?.role || "",
  blockStatus: selectedUser?.user?.blockStatus || selectedUser?.blockStatus || "",
  subscriptionEndDate: selectedUser?.subscriber?.subscriptionEndDate || selectedUser?.subscriptionEndDate || "",
  currentPlan: selectedUser?.subscriber?.currentPlan || selectedUser?.currentPlan || "",
  paymentMethod: selectedUser?.payment?.paymentType || selectedUser?.paymentMethod || "",
  paymentStatus: selectedUser?.payment?.transactionStatus || selectedUser?.paymentStatus || "",
});

const formatDateInput = (value) => {
  if (!value) return "";
  return String(value).slice(0, 10);
};

const Field = ({ label, children }) => (
  <label className="block">
    <span className="text-sm font-semibold text-slate-700">{label}</span>
    <div className="mt-1">{children}</div>
  </label>
);

const inputClass =
  "h-11 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-700 outline-none transition focus:border-sky-400 focus:ring-2 focus:ring-sky-100 disabled:bg-slate-50 disabled:text-slate-500";

const UserEdit = ({ selectedUser }) => {
  const userData = useMemo(() => getUserData(selectedUser), [selectedUser]);
  const [newRole, setNewRole] = useState("");
  const [blockStatus, setBlockStatus] = useState("");
  const [subscriptionEndDate, setSubscriptionEndDate] = useState("");
  const [plan, setPlan] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("");
  const [paymentStatus, setPaymentStatus] = useState("");

  useEffect(() => {
    setNewRole(userData.role);
    setBlockStatus(userData.blockStatus);
    setSubscriptionEndDate(formatDateInput(userData.subscriptionEndDate));
    setPlan(userData.currentPlan);
    setPaymentMethod(userData.paymentMethod);
    setPaymentStatus(userData.paymentStatus);
  }, [userData]);

  const handleUpdateUser = async () => {
    const isConfirmed = window.confirm("Are you sure you want to update this user?");

    if (!isConfirmed) {
      return;
    }

    const userDataPayload = {
      userId: userData.id,
      ...(newRole && newRole !== userData.role && { role: newRole }),
      ...(blockStatus && blockStatus !== userData.blockStatus && { blockStatus }),
      ...(subscriptionEndDate &&
        subscriptionEndDate !== formatDateInput(userData.subscriptionEndDate) && { subscriptionEndDate }),
      ...(plan && plan !== userData.currentPlan && { currentPlan: plan }),
      ...(paymentMethod && paymentMethod !== userData.paymentMethod && { paymentMethod }),
      ...(paymentStatus && paymentStatus !== userData.paymentStatus && { paymentStatus }),
    };

    Object.keys(userDataPayload).forEach((key) => userDataPayload[key] === undefined && delete userDataPayload[key]);

    try {
      const response = await fetch("/api/users/update", {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(userDataPayload),
      });

      const result = await response.json();

      if (response.ok) {
        alert("User updated successfully!");
      } else {
        console.error("Error:", result.message);
        alert("Failed to update user.");
      }
    } catch (error) {
      console.error("Error:", error);
      alert("Something went wrong. Please try again.");
    }
  };

  return (
    <section className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
      <div className="mb-5 flex flex-col justify-between gap-3 sm:flex-row sm:items-center">
        <div>
          <h2 className="text-lg font-bold text-slate-950">Edit User</h2>
          <p className="text-sm text-slate-500">Update account access, plan, and payment state.</p>
        </div>
        <span className="rounded-full bg-slate-100 px-3 py-1.5 text-sm font-semibold text-slate-600">
          {userData.email || "No email"}
        </span>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
        <Field label="Name">
          <input type="text" className={inputClass} value={userData.name} disabled />
        </Field>

        <Field label="Role">
          <input
            type="text"
            className={inputClass}
            value={newRole}
            onChange={(event) => setNewRole(event.target.value)}
            placeholder="Enter new role"
          />
        </Field>

        <Field label="Block Status">
          <select className={inputClass} value={blockStatus} onChange={(event) => setBlockStatus(event.target.value)}>
            <option value="">Select block status</option>
            <option value="Active">Active</option>
            <option value="Blocked">Blocked</option>
          </select>
        </Field>

        <Field label="Subscription End Date">
          <input
            type="date"
            className={inputClass}
            value={subscriptionEndDate}
            onChange={(event) => setSubscriptionEndDate(event.target.value)}
          />
        </Field>

        <Field label="Current Plan">
          <input type="text" className={inputClass} value={plan} onChange={(event) => setPlan(event.target.value)} />
        </Field>

        <Field label="Payment Method">
          <input
            type="text"
            className={inputClass}
            value={paymentMethod}
            onChange={(event) => setPaymentMethod(event.target.value)}
          />
        </Field>

        <Field label="Payment Status">
          <select className={inputClass} value={paymentStatus} onChange={(event) => setPaymentStatus(event.target.value)}>
            <option value="">Select payment status</option>
            <option value="Paid">Paid</option>
            <option value="Pending">Pending</option>
            <option value="Refunded">Refunded</option>
          </select>
        </Field>
      </div>

      <div className="mt-5 flex justify-end">
        <button
          className="rounded-lg bg-sky-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-sky-700"
          onClick={handleUpdateUser}
        >
          Update User
        </button>
      </div>
    </section>
  );
};

export default UserEdit;
